import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { setEventSortingFilter } from "../../../../redux/events/actions";
import CustomSelect from "../../../forms/selects/CustomSelect";

/**
 * @interface for the EventSortingField component props.
 */
interface EventSortingProps {
  thematicFiltersEnabled: boolean;
}

/**
 * EventSortingField
 *
 * A filter component for sorting events. It allows users to select from
 * alphabetical or thematic sorting, or reset the filter using the "-" option.
 *
 * @param {EventSortingProps} props - Component props
 * @param {boolean} props.thematicFiltersEnabled - Whether thematic sorting is available
 * @returns {JSX.Element} - A CustomSelect dropdown for event sorting
 */
const EventSortingField = ({ thematicFiltersEnabled }: EventSortingProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation(["events"]);
  const [value, setValue] = useState<string>("");

  // Translated labels for sorting options
  const alphabetical = t("eventFilter.sortingFilters.alphabeticalSorting", {
    ns: "events",
  });
  const thematic = t("eventFilter.sortingFilters.thematicSorting", {
    ns: "events",
  });
  const label = t("eventFilter.sortingFilters.label", {
    ns: "events",
  });

  /**
   * Options available in the dropdown.
   * Includes a reset option "-", alphabetical, and optional thematic sorting.
   */
  const options = [
    { key: "none", value: "", label: "-" },
    { key: "name", value: "name", label: alphabetical },
    ...(!thematicFiltersEnabled
      ? [{ key: "topic", value: "topic", label: thematic }]
      : []),
  ];

  /**
   * Handles dropdown selection changes.
   * Updates local state and dispatches the selected sorting filter to Redux.
   *
   * @param event - The change event from the select input
   */
  const handleChange = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    const selectedValue = event.target.value as string;
    setValue(selectedValue);
    dispatch(setEventSortingFilter(selectedValue));
  };

  return (
    <CustomSelect
      id="sorting"
      labelId="sorting"
      name="sorting"
      label={label}
      options={options}
      value={value}
      onChange={handleChange}
      defaultText={label}
    />
  );
};

export default EventSortingField;
